// Helper function to handle binary file downloads (XLSX, CSV, etc.)
export function handleDownload(buffer: ArrayBuffer | Blob, fileName: string, mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
	const blob = new Blob([buffer], {
		type: mimeType,
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

/**
 * There's no cross-browser "print dialog closed" event, so the hidden iframe (and its object URL) is
 * cleaned up after a generous fixed delay instead — long enough that a user who's actually printing
 * won't have it vanish mid-dialog, short enough not to leak indefinitely if they just close the tab.
 */
const PRINT_IFRAME_CLEANUP_MS = 60_000;

/**
 * Opens the browser's native print dialog for a PDF blob already built on the client — no download,
 * no navigation away from the page. Loads the PDF into a hidden iframe (the browser's own PDF viewer
 * renders it) and calls `print()` on that frame once it's ready; the iframe's `contentWindow.print()`
 * only reliably shows the actual PDF content — printing the host page or an empty frame directly is
 * what a naive `window.print()` here would do instead.
 *
 * Returns a promise so callers can actually catch a failure: the iframe's load (and any error inside
 * `focus`/`print`) happens asynchronously, well after this function would otherwise have already
 * returned — a caller wrapping a fire-and-forget call in its own `try/catch` never sees an error that
 * surfaces later in `onload`, and reports success regardless of whether printing ever actually started.
 * This resolves once `print()` has been invoked (not once the native dialog closes — no browser exposes
 * that), and rejects if the iframe fails to load, has no `contentWindow`, or `focus`/`print` throws.
 */
export function printPdfBlob(blob: Blob): Promise<void> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(blob);
		const iframe = document.createElement("iframe");
		iframe.style.position = "fixed";
		iframe.style.width = "0";
		iframe.style.height = "0";
		iframe.style.border = "none";
		iframe.src = url;

		iframe.onload = () => {
			try {
				if (!iframe.contentWindow) throw new Error("Print iframe has no contentWindow");
				iframe.contentWindow.focus();
				iframe.contentWindow.print();
				resolve();
			} catch (error) {
				reject(error);
			}
		};
		iframe.onerror = () => reject(new Error("Print iframe failed to load the PDF"));

		document.body.appendChild(iframe);
		// Scheduled unconditionally (not from onload/onerror) so a hung iframe that never fires either
		// event still gets cleaned up — the whole reason this is a fixed delay rather than an event.
		window.setTimeout(() => {
			document.body.removeChild(iframe);
			URL.revokeObjectURL(url);
		}, PRINT_IFRAME_CLEANUP_MS);
	});
}
